import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Assets - Logo animado nuevo
const LOGO_VIDEO = '/assets/logo/realimenta-header-video.mp4';

const navLinks = [
  { href: '/', label: 'Inicio' },
  { href: '/servicios', label: 'Servicios' },
  { href: '/como-funciona', label: 'Cómo funciona' },
  { href: '/sobre-realimenta', label: 'Sobre REALimenta' },
  { href: '/contacto', label: 'Contacto' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: '#EDF2EA',
        boxShadow: isScrolled ? '0 2px 16px rgba(61,107,79,0.08)' : 'none'
      }}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Video del logo animado con fondo transparente */}
          <Link
            to="/"
            className="flex items-center flex-shrink-0"
            data-testid="header-logo-link"
          >
            <div className="relative h-12">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              >
                <source src="/assets/logo/video-de-heroe-transparent.webm" type="video/webm" />
                <source src="/assets/logo/video-de-heroe.mp4" type="video/mp4" />
              </video>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  location.pathname === link.href
                    ? 'bg-[#3D6B4F]/15 text-[#3D6B4F]'
                    : 'text-[#526D5B] hover:text-[#3D6B4F] hover:bg-[#3D6B4F]/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="flex items-center gap-4">
            <Link
              to="/como-funciona"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200"
              style={{
                backgroundColor: '#3D6B4F',
                color: 'white',
                boxShadow: '0 2px 8px rgba(61,107,79,0.3)'
              }}
              data-testid="header-cta-button"
            >
              Reservar consulta
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-[#3D6B4F]/10 transition-colors"
              aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#1A2E22]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1A2E22]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden backdrop-blur-md border-t relative z-50"
            style={{ backgroundColor: '#EDF2EA', borderColor: 'rgba(61,107,79,0.1)' }}
            data-testid="mobile-menu"
          >
            <nav className="section-container py-6 flex flex-col gap-2 relative z-50">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-link-${link.href.replace('/', '') || 'home'}`}
                  className={`px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 relative z-50 ${
                    location.pathname === link.href
                      ? 'bg-[#3D6B4F]/15 text-[#3D6B4F]'
                      : 'text-[#526D5B] hover:text-[#3D6B4F] hover:bg-[#3D6B4F]/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/como-funciona"
                className="flex items-center justify-center gap-2 mt-4 px-6 py-3 rounded-full font-semibold text-white relative z-50"
                style={{ backgroundColor: '#3D6B4F' }}
                data-testid="mobile-cta-button"
              >
                Reservar consulta
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
